import { useState, useEffect } from "react";
import { useForm } from "@inertiajs/react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useBasePath } from "@/lib/paths";
import { NomineeShareholderProtectedPerson } from "@/types";
import { CheckCircle2 } from "lucide-react";
import { LocationSelector } from "@/components/ui/LocationSelector";
import { DocumentUploader } from "@/components/shared/DocumentUploader";

type LocationItem = { id: string; code: string; name: string };
type StateItem = LocationItem & { country_id: string };
type LocalAreaItem = LocationItem & { state_id: string };

type Props = {
  shareholderId: string;
  person: NomineeShareholderProtectedPerson | null;
  countries: LocationItem[];
  states: StateItem[];
  localAreas: LocalAreaItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  basePath?: string;
};

export function EditProtectedPersonSheet({
  shareholderId,
  person,
  countries,
  states,
  localAreas,
  open,
  onOpenChange,
  basePath = "",
}: Props) {
  const defaultBasePath = useBasePath();
  const actualBasePath = basePath || defaultBasePath;
  const [success, setSuccess] = useState(false);

  const form = useForm({
    person_kind: "individual",
    full_name: "",
    legal_name: "",
    email: "",
    phone: "",
    country_code: "",
    state_province: "",
    city: "",
    postal_code: "",
    street_address_1: "",
    street_address_2: "",
    date_of_birth: "",
    nationality: "",
    id_document_number: "",
    ownership_percentage: "",
    shares_held: "",
    share_class: "",
  });

  useEffect(() => {
    if (person && open) {
      form.setData({
        person_kind: person.person_kind,
        full_name: person.full_name,
        legal_name: person.legal_name || "",
        email: person.email || "",
        phone: person.phone || "",
        country_code: person.country_code || "",
        state_province: person.state_province || "",
        city: person.city || "",
        postal_code: person.postal_code || "",
        street_address_1: person.street_address_1 || "",
        street_address_2: person.street_address_2 || "",
        date_of_birth: person.date_of_birth || "",
        nationality: person.nationality || "",
        id_document_number: person.id_document_number || "",
        ownership_percentage: person.ownership_percentage?.toString() || "",
        shares_held: person.shares_held?.toString() || "",
        share_class: person.share_class || "",
      });
    }
  }, [person, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!person) return;

    form.patch(
      `${actualBasePath}/nominee_shareholders/${shareholderId}/protected_persons/${person.id}`,
      {
        onSuccess: () => {
          setSuccess(true);
          setTimeout(() => {
            setSuccess(false);
            onOpenChange(false);
          }, 1000);
        },
      },
    );
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.clearErrors();
      setSuccess(false);
    }
    onOpenChange(open);
  };

  if (success) {
    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="right" className="sm:max-w-lg">
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-sm font-medium text-foreground">
              Protected person updated successfully
            </p>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  if (!person) return null;

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit Protected Person</SheetTitle>
          <SheetDescription>Update beneficial owner details</SheetDescription>
        </SheetHeader>

        <form
          id="person-form"
          onSubmit={handleSubmit}
          className="space-y-5 px-4"
        >
          <div>
            <Label className="text-sm font-medium mb-2 block">
              Person Type
            </Label>
            <RadioGroup
              value={form.data.person_kind}
              onValueChange={(value) => form.setData("person_kind", value)}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="individual" id="sheet-individual" />
                <Label
                  htmlFor="sheet-individual"
                  className="text-sm font-normal"
                >
                  Individual
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="entity" id="sheet-entity" />
                <Label htmlFor="sheet-entity" className="text-sm font-normal">
                  Entity
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Ownership Fields */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Ownership Details</Label>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">
                  Ownership %
                </Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={form.data.ownership_percentage}
                  onChange={(e) =>
                    form.setData("ownership_percentage", e.target.value)
                  }
                  placeholder="e.g. 50"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">
                  Shares Held
                </Label>
                <Input
                  type="number"
                  min="0"
                  value={form.data.shares_held}
                  onChange={(e) => form.setData("shares_held", e.target.value)}
                  placeholder="e.g. 1000"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">
                  Share Class
                </Label>
                <Input
                  value={form.data.share_class}
                  onChange={(e) => form.setData("share_class", e.target.value)}
                  placeholder="e.g. Common"
                />
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium">Name</Label>
            <div className="space-y-1.5">
              <Input
                value={form.data.full_name}
                onChange={(e) => form.setData("full_name", e.target.value)}
                placeholder={
                  form.data.person_kind === "entity"
                    ? "Entity legal name"
                    : "Full name"
                }
                required
              />
              {form.errors.full_name && (
                <p className="text-[12px] text-destructive">
                  {form.errors.full_name}
                </p>
              )}
            </div>
            {form.data.person_kind === "entity" && (
              <Input
                value={form.data.legal_name}
                onChange={(e) => form.setData("legal_name", e.target.value)}
                placeholder="DBA name (optional)"
              />
            )}
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium">Contact</Label>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Input
                  type="email"
                  value={form.data.email}
                  onChange={(e) => form.setData("email", e.target.value)}
                  placeholder="Email"
                />
                {form.errors.email && (
                  <p className="text-[12px] text-destructive">
                    {form.errors.email}
                  </p>
                )}
              </div>
              <Input
                type="tel"
                value={form.data.phone}
                onChange={(e) => form.setData("phone", e.target.value)}
                placeholder="Phone"
              />
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium">Address</Label>
            <Input
              value={form.data.street_address_1}
              onChange={(e) => form.setData("street_address_1", e.target.value)}
              placeholder="Street address"
            />
            <Input
              value={form.data.street_address_2}
              onChange={(e) => form.setData("street_address_2", e.target.value)}
              placeholder="Apt, suite, etc. (optional)"
            />
            <LocationSelector
              countries={countries}
              states={states}
              localAreas={localAreas}
              countryCode={form.data.country_code}
              stateProvince={form.data.state_province}
              city={form.data.city}
              onChange={({ country_code, state_province, city }) => {
                form.setData((d) => ({
                  ...d,
                  country_code,
                  state_province,
                  city,
                }));
              }}
            />
            <Input
              value={form.data.postal_code}
              onChange={(e) => form.setData("postal_code", e.target.value)}
              placeholder="ZIP code"
              className="h-8 text-sm"
            />
          </div>

          {form.data.person_kind === "individual" && (
            <div className="space-y-3">
              <Label className="text-sm font-medium">
                Personal Information
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">
                    Date of Birth
                  </Label>
                  <Input
                    type="date"
                    value={form.data.date_of_birth}
                    onChange={(e) =>
                      form.setData("date_of_birth", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">
                    Nationality
                  </Label>
                  <Input
                    value={form.data.nationality}
                    onChange={(e) =>
                      form.setData("nationality", e.target.value)
                    }
                    placeholder="e.g. US"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">
                  ID Document Number
                </Label>
                <Input
                  value={form.data.id_document_number}
                  onChange={(e) =>
                    form.setData("id_document_number", e.target.value)
                  }
                  placeholder="Passport or ID number"
                />
              </div>
            </div>
          )}

          <div className="sticky bottom-0 bg-background border-t p-4 -mx-4 mt-4">
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="person-form"
                className="flex-1"
                disabled={form.processing}
              >
                {form.processing ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </form>

        {/* KYC Documents — outside the form to avoid nested form HTML violation */}
        {person && (
          <div className="border-t pt-4 mt-2 px-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
              KYC Documents
            </p>
            <DocumentUploader
              title="KYC Documents"
              documents={person.documents || []}
              uploadPath={`${actualBasePath}/nominee_shareholders/${shareholderId}/protected_persons/${person.id}/documents`}
              deletePath={(docId) =>
                `${actualBasePath}/nominee_shareholders/${shareholderId}/protected_persons/${person.id}/documents/${docId}`
              }
            />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
